import type { Component } from "solid-js";

export interface PanelEntry {
  id: string;
  side: "l" | "r";
  featureGate: string | null;
  label: string;
  title: string;
  iconKey?: string;
  component?: Component;
}

// Pour A1 : registre sans composants. Chaque agent B1-B4 ajoutera ses composants
// via un mécanisme d'enregistrement : `registerPanel(id, Component)`.
const COMPONENTS: Record<string, Component> = {};

export function registerPanel(id: string, component: Component): void {
  COMPONENTS[id] = component;
}

const ENTRIES: Omit<PanelEntry, "component">[] = [
  // ── Chat opérateur ⇄ utilisateur assisté (tray client). EN PREMIER pour
  //    qu'il soit immédiatement visible (pas de scroll horizontal dans le dock).
  { id: "chat", side: "l", featureGate: null, label: "Chat", title: "Chat avec l'utilisateur" },
  { id: "powershell", side: "r", featureGate: null, label: "PS", title: "PowerShell" },
  { id: "services", side: "r", featureGate: null, label: "Svcs", title: "Services Windows" },
  { id: "process", side: "r", featureGate: null, label: "Proc.", title: "Processus" },
  { id: "autoruns", side: "r", featureGate: null, label: "Auto", title: "Autoruns" },
  // ── Panels cross-host (admin) déplacés hors session — gated derrière une
  //    feature `_admin` que l'agent n'annonce jamais. Le code reste pour
  //    permettre un futur portage côté /console/admin. ──────────────────
  { id: "hosts", side: "r", featureGate: "_admin", label: "Hotes", title: "Modules hotes" },
  { id: "mcpSessions", side: "r", featureGate: "_admin", label: "MCP", title: "Sessions MCP" },
  { id: "users", side: "r", featureGate: "_admin", label: "Users", title: "Utilisateurs (admin)" },
  { id: "ad", side: "r", featureGate: "activeDirectory", label: "AD", title: "Active Directory" },
  { id: "iis", side: "r", featureGate: "iis", label: "IIS", title: "IIS" },
  { id: "docker", side: "r", featureGate: "docker", label: "Docker", title: "Docker" },
  { id: "smb", side: "r", featureGate: "smb", label: "SMB", title: "Partages SMB+DFS" },
  { id: "exchange", side: "r", featureGate: "exchange", label: "Exch.", title: "Exchange" },
  { id: "hyperv", side: "r", featureGate: "hyperv", label: "HyperV", title: "Hyper-V" },
  { id: "cluster", side: "r", featureGate: "failoverCluster", label: "Cluster", title: "Failover Cluster" },
  { id: "dns", side: "r", featureGate: "dnsServer", label: "DNS", title: "DNS" },
  { id: "dhcp", side: "r", featureGate: "dhcpServer", label: "DHCP", title: "DHCP" },
  { id: "schtasks", side: "r", featureGate: "scheduledTasks", label: "Tasks", title: "Taches planifiees" },
  { id: "print", side: "r", featureGate: "printServer", label: "Impr.", title: "Imprimantes" },
  { id: "wsus", side: "r", featureGate: "wsus", label: "WSUS", title: "WSUS" },
  { id: "certs", side: "r", featureGate: "certStore", label: "Certs", title: "Certificats" },
  { id: "localusers", side: "r", featureGate: "localAccounts", label: "Cptes", title: "Comptes locaux" },
  { id: "firewall", side: "r", featureGate: "firewall", label: "FW", title: "Firewall" },
  { id: "aicli", side: "r", featureGate: "tmux", label: "AI", title: "Sessions AI CLI" },
  { id: "ubuntuTerminal", side: "r", featureGate: "ubuntuShell", label: "Term", title: "Terminal Ubuntu" },
  { id: "ubuntuLogs", side: "r", featureGate: "linuxHost", label: "Logs", title: "Logs Ubuntu" },
  {
    id: "files",
    side: "r",
    featureGate: null,
    label: "Fichiers",
    title: "Explorateur de fichiers (local ↔ host, transfert, reprise)",
  },
  {
    id: "versions",
    side: "r",
    featureGate: null,
    label: "Versions",
    title: "Versions de fichiers (VSS)",
  },
  { id: "choco", side: "r", featureGate: "choco", label: "Choco", title: "Chocolatey" },
  { id: "pktmon", side: "r", featureGate: null, label: "Pkt", title: "Packet Monitor" },
  { id: "netinspect", side: "r", featureGate: null, label: "HTTPS", title: "Inspecteur HTTPS" },
  { id: "lanbrowser", side: "r", featureGate: null, label: "LAN", title: "LAN Browser" },
  { id: "watchguard", side: "r", featureGate: null, label: "WG", title: "WatchGuard" },
  { id: "watchguard-rules", side: "r", featureGate: null, label: "WG Règles", title: "WatchGuard · Règles" },
  {
    id: "watchguard-traffic",
    side: "r",
    featureGate: null,
    label: "WG Traffic",
    title: "WatchGuard · Traffic Monitor",
  },
  { id: "watchguard-links", side: "r", featureGate: null, label: "WG Links", title: "WatchGuard · Link monitors" },
  { id: "watchguard-users", side: "r", featureGate: null, label: "WG Auth", title: "WatchGuard · Authentifiés" },
  { id: "watchguard-config", side: "r", featureGate: null, label: "WG Config", title: "WatchGuard · Configuration" },
  { id: "watchguard-tcpdump", side: "r", featureGate: null, label: "WG Dump", title: "WatchGuard · tcpdump" },
  { id: "plugins", side: "r", featureGate: null, label: "Plug.", title: "Plugins" },
  { id: "operators", side: "r", featureGate: null, label: "Opér.", title: "Opérateurs" },
  // Nouveaux : 6 plugins backend C# qui n'avaient pas de panel.
  { id: "handles", side: "r", featureGate: null, label: "Hdl.", title: "Handles ouverts" },
  { id: "registry", side: "r", featureGate: null, label: "Reg.", title: "Registre Windows" },
  { id: "software", side: "r", featureGate: null, label: "Soft.", title: "Logiciels installés" },
  { id: "sql", side: "r", featureGate: "sqlServer", label: "SQL", title: "SQL Server" },
  { id: "ssh", side: "r", featureGate: null, label: "SSH", title: "Sessions SSH" },
  // ── Panels CLI (un par adapter : claude/codex/kimi/gemini/opencode/hermes/openclaw).
  //    Tous gated derrière la feature "cli" annoncée par le plugin C# RemoteWS.Plugin.Cli.
  //    Le wrapper partagé <CliPanel cli={...} /> (panels/cli/cli-panel.tsx) porte
  //    toute la logique ; chaque fichier cli-<id>.tsx est un stub d'enregistrement.
  { id: "cli-claude", side: "r", featureGate: "cli", label: "Claude", title: "Claude Code" },
  { id: "cli-codex", side: "r", featureGate: "cli", label: "Codex", title: "OpenAI Codex" },
  { id: "cli-kimi", side: "r", featureGate: "cli", label: "Kimi", title: "Kimi Code" },
  { id: "cli-gemini", side: "r", featureGate: "cli", label: "Gemini", title: "Gemini CLI" },
  { id: "cli-opencode", side: "r", featureGate: "cli", label: "OpenCode", title: "OpenCode" },
  { id: "cli-hermes", side: "r", featureGate: "cli", label: "Hermes", title: "Hermes" },
  { id: "cli-openclaw", side: "r", featureGate: "cli", label: "OpenClaw", title: "OpenClaw" },
  { id: "computeruse", side: "r", featureGate: null, label: "CU", title: "Computer Use" },
  { id: "winupdate", side: "r", featureGate: null, label: "WU", title: "Windows Update" },
  // Mode RDP — bureau Windows natif via client IronRDP compilé en WASM.
  { id: "rdp", side: "r", featureGate: "rdp", label: "RDP", title: "Bureau RDP natif" },
  // Lot 4 du chantier politiques-host — affiche policy effective + access live + RDS.
  { id: "policy", side: "r", featureGate: null, label: "Policy", title: "Politiques hôte" },
  // ── Panels "aperçu" (plugin agent à livrer) — affichent un état vide
  //    documenté (PanelPreview) tant que la feature gate est absente.
  //    featureGate: null pour qu'ils apparaissent et soient visibles à
  //    qui voudra implémenter le plugin associé.
  { id: "eventviewer", side: "r", featureGate: null, label: "Logs", title: "Journaux Windows" },
  { id: "storage", side: "r", featureGate: null, label: "Disques", title: "Stockage / Disques" },
  { id: "network", side: "r", featureGate: null, label: "Network", title: "Network" },
  { id: "network-wifi", side: "r", featureGate: null, label: "WiFi", title: "WiFi" },
  { id: "network-lan", side: "r", featureGate: null, label: "Scan", title: "Découverte LAN" },
  { id: "network-diag", side: "r", featureGate: null, label: "Diag", title: "Diagnostic réseau" },
  { id: "network-scan", side: "r", featureGate: null, label: "Scan", title: "Scanner de services" },
  { id: "rdpsessions", side: "r", featureGate: null, label: "Sess.", title: "Sessions utilisateurs (RDP / console)" },
  { id: "defender", side: "r", featureGate: null, label: "Def.", title: "Defender / Antivirus" },
  { id: "drivers", side: "r", featureGate: null, label: "Drv.", title: "Drivers / Périphériques" },
  { id: "hotfix", side: "r", featureGate: null, label: "Hotfix", title: "Hotfix / KB installées" },
  // Module debug isolé : qualité de prise en main (fps/encodage/cpu/conversion),
  // snapshot CPU/mémoire des process RemoteWS, état des plugins.
  { id: "debug", side: "r", featureGate: null, label: "Debug", title: "Debug / Diagnostics" },
];

// Cache des entries matérialisées : le Proxy retournait un nouvel objet à
// chaque accès, ce qui cassait l'identité référentielle et forçait SolidJS à
// remonter les panels à chaque recalcul de sections().
const entryCache = new Map<string, PanelEntry>();

function makeEntry(e: Omit<PanelEntry, "component">): PanelEntry {
  let entry = entryCache.get(e.id);
  if (!entry) {
    entry = { ...e, component: COMPONENTS[e.id] };
    entryCache.set(e.id, entry);
  }
  // Si un composant est enregistré après coup, on met à jour la propriété sur
  // le MÊME objet pour ne pas invalider l'identité référentielle.
  entry.component = COMPONENTS[e.id];
  return entry;
}

export const PANEL_REGISTRY: PanelEntry[] = new Proxy(ENTRIES as PanelEntry[], {
  get(target, prop) {
    if (typeof prop === "string" && /^\d+$/.test(prop)) {
      const e = target[parseInt(prop, 10)];
      return e ? makeEntry(e) : undefined;
    }
    const v = Reflect.get(target, prop);
    if (prop === Symbol.iterator) {
      return function* () {
        for (const e of target) yield makeEntry(e);
      };
    }
    return v;
  },
}) as PanelEntry[];
